import localforage from 'localforage';
import * as Sentry from '@sentry/react';
import { isObservabilityEnabled } from './observabilityConfig';

const TTL = 48 * 60 * 60 * 1000;
const LIMIT = 100;
const OUTCOMES = new Set(['sending', 'deferred_offline', 'retrying', 'acknowledgement_unknown', 'server_accepted', 'accepted_reconciliation_failed', 'validation_rejected', 'failed_queue', 'local_write_failed', 'interrupted_pending']);
const INCIDENTS = new Set(['local_write_failed', 'failed_queue', 'accepted_reconciliation_failed']);
const store = localforage.createInstance({ name: 'mSalesOperationDiagnostics', storeName: 'checkpoints' });
const documentId = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
let actor = null;
let work = Promise.resolve();
let queued = 0;
let ownsDocumentLock = false;
let lockRequested = false;

const isCurrentActor = (owner) => actor === owner;
const safeId = (value) => typeof value === 'string' && /^[a-zA-Z0-9_.:-]{1,160}$/.test(value) ? value : null;
export function setOperationDiagnosticActor(id) {
  actor = safeId(id == null ? null : String(id));
}

function schedule(fn) {
  try {
    if (!isObservabilityEnabled('outcomes') || !actor || queued >= LIMIT) return;
    const owner = actor;
    queued += 1;
    work = work.then(() => {
      if (actor !== owner || !isObservabilityEnabled('outcomes')) return undefined;
      const run = () => actor === owner && isObservabilityEnabled('outcomes') ? fn(owner) : undefined;
      if (typeof navigator !== 'undefined' && navigator.locks) {
        return navigator.locks.request(`msales-diagnostics-writes-${owner}`, run);
      }
      return run();
    }).catch(() => {}).then(() => { queued -= 1; });
  } catch (_) {}
}

function claimDocument() {
  if (lockRequested) return;
  lockRequested = true;
  try {
    if (!navigator.locks) return;
    navigator.locks.request(`msales-diagnostics-${documentId}`, () => {
      ownsDocumentLock = true;
      return new Promise(() => {});
    }).catch(() => {});
  } catch (_) {}
}

async function prune() {
  const rows = [];
  await store.iterate((value, key) => { rows.push({ value, key }); });
  rows.sort((a, b) => b.value.at - a.value.at);
  await Promise.all(rows.filter((row, index) => index >= LIMIT - 1 || Date.now() - row.value.at > TTL).map(({ key }) => store.removeItem(key)));
}

function emit(record, incident) {
  try {
    const data = { operation_id: record.id, outcome: record.outcome, source: record.source, reason: record.reason, server_acknowledged: record.accepted === true };
    if (incident) {
      Sentry.captureEvent({ message: 'mSales operation outcome', level: 'error', tags: { observability_outcome: 'true', outcome: record.outcome, operation: 'order' }, fingerprint: ['msales-operation-outcome', record.outcome], contexts: { operation_outcome: data } });
    } else {
      Sentry.addBreadcrumb({ category: 'operation.outcome', level: 'info', message: record.outcome, data });
    }
  } catch (_) {}
}

export function isUnknownAcknowledgement(error) {
  try { return Boolean(error?.networkError || /timeout|network|failed to fetch/i.test(error?.message || '')); } catch (_) { return false; }
}

export function recordOperationOutcome(operation, outcome, details = {}) {
  try {
    if (!isObservabilityEnabled('outcomes') || !actor) return;
    if (operation?.type !== 'INSERT_ORDER' || !OUTCOMES.has(outcome)) return;
    const id = safeId(String(operation.id || ''));
    if (!id) return;
    claimDocument();
    // Only fixed labels enter telemetry; mutation variables and exception text never do.
    const source = ['checkout', 'automatic', 'manual', 'service', 'recovery'].includes(details.source) ? details.source : 'service';
    const reason = ['network', 'retry_limit', 'validation', 'storage', 'reconciliation', 'pending_queue', 'no_ack'].includes(details.reason) ? details.reason : 'none';
    schedule(async (owner) => {
      const key = `${owner}:${id}`;
      const previous = await store.getItem(key);
      const reports = previous && Date.now() - previous.at < TTL ? previous.reports || [] : [];
      const incident = INCIDENTS.has(outcome) && !reports.includes(outcome);
      const record = { id, actor: owner, documentId, recoverable: ownsDocumentLock, at: Date.now(), outcome, source, reason, accepted: outcome === 'server_accepted' || outcome === 'accepted_reconciliation_failed' || Boolean(previous?.accepted), reports: incident ? [...reports, outcome].slice(-3) : reports };
      await prune();
      await store.setItem(key, record);
      if (actor !== owner || !isObservabilityEnabled('outcomes')) return;
      if (incident || !INCIDENTS.has(outcome)) emit(record, incident);
    });
  } catch (_) {}
}

export function reconcileOperationDiagnostics(operations) {
  try {
    const pending = new Set((operations || []).filter((op) => op.type === 'INSERT_ORDER').map((op) => String(op.id)));
    schedule(async (owner) => {
      if (typeof navigator === 'undefined' || !navigator.locks) return;
      const rows = [];
      await store.iterate((value, key) => { if (value.actor === owner && value.documentId !== documentId && value.recoverable && value.outcome === 'sending' && Date.now() - value.at < TTL && pending.has(value.id)) rows.push({ value, key }); });
      for (const { value, key } of rows.slice(0, LIMIT)) {
        await navigator.locks.request(`msales-diagnostics-${value.documentId}`, { ifAvailable: true }, async (lock) => {
          if (!lock || !isCurrentActor(owner) || !isObservabilityEnabled('outcomes')) return;
          const current = await store.getItem(key);
          if (!current || current.at !== value.at) return;
          const record = { ...current, outcome: 'interrupted_pending', source: 'recovery', reason: 'pending_queue', at: Date.now() };
          await store.setItem(key, record);
          emit(record, false);
        });
      }
    });
  } catch (_) {}
}

export function settleOperationDiagnostics() { return work; }
